import { useCallback, useEffect, useState } from 'react';
import { Scale } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { isRebookingConflictError } from '@/lib/rebooking/api';
import { isLockConflictError } from '@/lib/locks/types';
import type { CabinClass, RebookingConflict } from '@/lib/rebooking/types';
import type { DisruptionPnrGroupRow } from '@/lib/disruptions/types';
import { routingDisruptionUrn, type PnrGroupScope } from '@/lib/pnr/scope';
import {
  useCancelGroupSelection,
  useExcludePnr,
  useOverrideGroupOption,
  useRejectGroupOptions,
} from '@/lib/disruptions/use-disruptions';
import { FlightPicker, offerFlightCode, offerProviderName } from '@/components/pnr/flight-picker';
import { searchDayOf } from '@/lib/flights/search-day';
import { carrierName, flightWireNumber } from '@/lib/flights/api';
import { groupFlightOffersKey } from '@/lib/rebooking/use-group-flight-offers';
import type { GroupFlightOffer, GroupFlightOffersView } from '@/lib/rebooking/types';
import { hasRole, Role } from '@nexa/auth';
import { useSession } from '@/auth/use-session';
import { formatPaxSummary, noOptionsReasonLabel } from './labels';

// The PNR group's confirm/override dialogs, extracted from roster-pnr-drawer.tsx so
// the dialog wrapper stays small. Each owns its own mutation + form state; the body
// opens them and feeds the conflict/lock callbacks. Behavior is unchanged.

const CABINS: CabinClass[] = ['ECONOMY', 'PREMIUM_ECONOMY', 'BUSINESS', 'FIRST_CLASS'];

/** No-options codes we can say something USEFUL about for a manual pick. An
 *  unknown code falls back to the generic line rather than inventing advice. */
const MANUAL_PICK_MEANING = new Set([
  'POLICY_DISABLED',
  'NO_INVENTORY',
  'FILTERED_OUT',
  'NO_ROUTE',
  'REJECTED_ALL',
  'HOLD_EXPIRED',
  'VENDOR_UNAVAILABLE',
  'OPTIONS_DEPARTED',
]);

/** Machine reasons the domain returns with a 422 VENDOR_VALIDATION_FAILED. */
const VENDOR_ERROR_REASONS = new Set(['FLIGHT_NOT_FOUND', 'NO_SEATS', 'ROUTE_REQUIRED']);

/** 409s of a pick whose listed offer is gone — the operator searches again. */
const OFFER_LAPSED_CODES = new Set(['OFFER_EXPIRED', 'OFFER_NOT_FOUND']);

/** A cabin the select knows, or `undefined` (a provider's own cabin label). */
function asCabin(value: string | undefined): CabinClass | undefined {
  const upper = value?.toUpperCase();
  return CABINS.find((c) => c === upper);
}

/** Status + JSON body of a failed write (a ky HTTPError, or a test double of one). */
async function errorBody(
  err: unknown,
): Promise<{
  status: number | undefined;
  body: { code?: string; message?: string; reason?: string; providerBacked?: boolean };
}> {
  const response = (err as { response?: { status?: number; clone?: () => { json(): Promise<unknown> } } })?.response;
  let body = {};
  try {
    body = ((await response?.clone?.().json()) ?? {}) as object;
  } catch {
    /* no JSON body */
  }
  return { status: response?.status, body };
}

/** Override-dialog mode: a free manual flight. `null` ⇒ the dialog is closed. */
export type OverrideMode = 'manual';

interface OverrideDialogProps {
  scope: PnrGroupScope;
  group: DisruptionPnrGroupRow;
  /** `null` = closed; `manual` = arbitrary flight. */
  mode: OverrideMode | null;
  onClose: () => void;
  onConflict: (c: RebookingConflict) => void;
  onLockConflict: (holderName: string) => void;
}

export function OverrideDialog({
  scope,
  group,
  mode,
  onClose,
  onConflict,
  onLockConflict,
}: OverrideDialogProps) {
  const { t } = useTranslation('roster');
  const { t: tNetwork } = useTranslation('network');
  const qc = useQueryClient();
  const override = useOverrideGroupOption(scope);
  const [picked, setPicked] = useState<GroupFlightOffer | null>(null);
  // The operator's cabin — unset until they change it, so the first search asks for the
  // group's OWN cabin (what generation asks for); the select then shows what the providers
  // were asked (`searchedCabin`).
  const [cabin, setCabin] = useState<CabinClass | undefined>(undefined);
  const [searchedCabin, setSearchedCabin] = useState<CabinClass | undefined>(undefined);
  const shownCabin = cabin ?? searchedCabin;
  const onSearched = useCallback(
    (query: GroupFlightOffersView['query']) => setSearchedCabin(asCabin(query.cabin)),
    [],
  );
  const [reason, setReason] = useState('');
  const [manualMode, setManualMode] = useState(false);
  const [manualFlightNumber, setManualFlightNumber] = useState('');
  const [manualDeparture, setManualDeparture] = useState('');
  // POL-14: declare when this override knowingly WAIVES a policy rule (privileged —
  // the BFF requires OPS_SUPERVISOR and 403s otherwise).
  const [waiveRule, setWaiveRule] = useState(false);
  const [waivedRule, setWaivedRule] = useState('');
  const [permError, setPermError] = useState<string | null>(null);
  // Vendor said no (422 VENDOR_VALIDATION_FAILED): the flight isn't in the
  // schedule / can't seat the party. Kept in-dialog so the operator can fix the
  // pick — or a supervisor can force it with the VENDOR_VALIDATION waiver. The
  // machine `reason` rides along so the copy can name the actual next step
  // instead of a flat "the vendor said no".
  const [vendorError, setVendorError] = useState<{
    message: string;
    reason?: string;
    /** A provider deployment: only a provider's offer can be assigned, so no waiver hint. */
    providerBacked?: boolean;
  } | null>(null);
  // The picked offer is gone (expired / withdrawn) or no provider sells the typed flight.
  const [offerError, setOfferError] = useState<string | null>(null);
  const { role } = useSession();
  const isSupervisor = hasRole(role, Role.OPS_SUPERVISOR);

  const open = mode !== null;

  // The engine's own verdict, carried INTO the dialog. Opening a manual override
  // on a PNR the engine ruled out is the normal case — but what a hand pick can
  // actually achieve depends entirely on WHY there was nothing to rank, and the
  // dialog used to say nothing at all. NO_INVENTORY in particular means the same
  // route on the same day will fail the vendor check the override runs, so the
  // operator has to widen the search or escalate; POLICY_DISABLED / FILTERED_OUT
  // mean flights exist and this hatch is exactly right.
  const terminalNoOptions =
    group.rebookingStatus === 'NO_OPTIONS' || group.rebookingStatus === 'NOT_APPLICABLE';
  const noOptionsWhy = terminalNoOptions
    ? noOptionsReasonLabel(group.noOptionsReason, t)
    : undefined;
  const meaningKey =
    group.noOptionsReason && MANUAL_PICK_MEANING.has(group.noOptionsReason)
      ? `dialogs.override.noOptionsMeaning.${group.noOptionsReason}`
      : 'dialogs.override.noOptionsMeaning.fallback';
  const vendorNextStepKey =
    vendorError?.reason && VENDOR_ERROR_REASONS.has(vendorError.reason)
      ? `dialogs.override.vendorErrorNext.${vendorError.reason}`
      : 'dialogs.override.vendorErrorNext.fallback';

  // Where the pax is stranded → the final destination, for the picker defaults.
  const finalDest =
    group.itinerary?.finalDestination ?? group.connection?.finalDestination ?? group.destination;
  // The search day: the disrupted departure's, or today once it is past — the replacement
  // leaves from now on, and a live schedule (the predictor roster: −2 h … +48 h) has
  // nothing for yesterday, so the picker opened on an empty list.
  const day = searchDayOf(group.departureAt);
  // Manual: along the disrupted route → final destination. A case opened without a
  // route still has the PNR's own itinerary to start from.
  const pickerOrigin = group.origin || (group.itinerary?.legs[0]?.origin ?? '');
  const pickerDestination = finalDest;

  useEffect(() => {
    if (mode === null) {
      setPicked(null);
      setCabin(undefined);
      setSearchedCabin(undefined);
      setReason('');
      setManualMode(false);
      setManualFlightNumber('');
      setManualDeparture('');
      setWaiveRule(false);
      setWaivedRule('');
      setPermError(null);
      setOfferError(null);
    }
  }, [mode]);

  const haveFlight = !!picked || (manualMode && !!manualFlightNumber.trim() && !!manualDeparture);
  // A typed flight needs a cabin; when the case's own could not be read, the operator says it.
  const valid = haveFlight && !!shownCabin && !!reason.trim() && !override.isPending;

  async function submit(): Promise<void> {
    // A picked offer travels as its `offerRef` — the domain materialises THAT provider
    // offer; the flight fields only echo it. A typed flight keeps the carrier-qualified
    // wire form ("H2102"): a bare number used to be attributed to the LATAM carrier.
    const first = picked?.segments[0];
    const last = picked?.segments[picked.segments.length - 1];
    const flightNumber = first
      ? flightWireNumber({ carrierCode: first.marketingAirline, flightNumber: first.flightNumber })
      : manualFlightNumber.trim();
    const departureAt = first ? first.departure : new Date(manualDeparture).toISOString();
    setPermError(null);
    setVendorError(null);
    setOfferError(null);
    try {
      await override.mutateAsync({
        subCaseUrn: group.subCaseUrn,
        caseUrn: group.caseUrn,
        flightNumber,
        departureAt,
        cabin: shownCabin!,
        reason: reason.trim(),
        ...(picked ? { offerRef: picked.offerRef } : {}),
        // Vendor validation inputs (§10.4): the route the flight must exist on
        // and the seats it must have for this party.
        origin: first ? first.origin : pickerOrigin,
        destination: last ? last.destination : pickerDestination,
        seats: group.paxCount,
        ...(waiveRule
          ? {
              overrideType: 'WAIVE_RULE' as const,
              ...(waivedRule.trim() ? { waivedRule: waivedRule.trim() } : {}),
            }
          : {}),
      });
      onClose();
    } catch (err) {
      if (isLockConflictError(err)) {
        onLockConflict(err.holderName);
        onClose();
      } else if (isRebookingConflictError(err)) {
        onConflict(err.conflict);
        onClose();
      } else {
        const { status, body } = await errorBody(err);
        if (status === 409) {
          if (body.code && OFFER_LAPSED_CODES.has(body.code)) {
            // The listed offer lapsed (provider expiry, withdrawn): drop the pick and re-ask.
            setPicked(null);
            setOfferError(t('dialogs.override.offerLapsed'));
            void qc.invalidateQueries({ queryKey: groupFlightOffersKey(group.subCaseUrn) });
          } else if (body.code === 'OPTION_DEPARTED') {
            // A stale offer whose flight already left (PO 2026-11-03): drop the pick and re-ask.
            setPicked(null);
            setOfferError(t('dialogs.override.optionDeparted'));
            void qc.invalidateQueries({ queryKey: groupFlightOffersKey(group.subCaseUrn) });
          } else if (body.code === 'SEAT_HOLD_UNAVAILABLE') {
            // The provider would not hold the seats (sold meanwhile): this offer is spent.
            setPicked(null);
            setOfferError(t('dialogs.override.holdRefused'));
            void qc.invalidateQueries({ queryKey: groupFlightOffersKey(group.subCaseUrn) });
          } else {
            // Any other refusal says what the domain said — never a silent dialog.
            setOfferError(body.message || t('dialogs.override.pickRefused'));
          }
        } else if (status === 422) {
          // The vendor refused the pick — keep the dialog open and say why.
          if (body.code === 'PROVIDER_OFFER_REQUIRED') {
            setOfferError(t('dialogs.override.providerOfferRequired'));
          } else {
            setVendorError({
              message: body.message || t('dialogs.override.vendorErrorFallback'),
              ...(body.reason ? { reason: body.reason } : {}),
              ...(body.providerBacked ? { providerBacked: true } : {}),
            });
          }
        } else if (status === 403) {
          // POL-14: the BFF refused a policy-rule waiver for this role — keep the dialog
          // open and tell the operator who can do it.
          setPermError(t('dialogs.override.permError'));
        } else if (status !== undefined && status >= 500) {
          // The provider's hold (or the offer / case read behind it) did not answer.
          setOfferError(t('dialogs.override.providerUnavailable'));
        } else {
          setOfferError(body.message || t('dialogs.override.pickFailed'));
        }
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      {/* #635: the search, the waiver and a vendor refusal can outgrow a short screen —
          the dialog scrolls inside the viewport so the confirm is always reachable. */}
      <DialogContent className="z-[60] max-h-[90dvh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('dialogs.override.titleManual', { pnr: group.pnrLabel })}</DialogTitle>
          <DialogDescription>{t('dialogs.override.descriptionManual')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          {/* Why there was nothing to rank, and what that means for picking by
              hand — said BEFORE the operator spends a search, not after a 422. */}
          {noOptionsWhy ? (
            <div
              className="space-y-1 rounded-md border border-dashed border-border bg-muted/40 p-2.5"
              data-testid="drawer-override-no-options-context"
            >
              <p className="flex items-start gap-1.5 text-muted-foreground">
                <Scale className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                <span>
                  <span className="font-medium text-foreground">
                    {t('dialogs.override.noOptionsHeading')}
                  </span>{' '}
                  {noOptionsWhy}
                </span>
              </p>
              <p className="pl-[20px] text-xs text-muted-foreground">{t(meaningKey)}</p>
            </div>
          ) : null}

          {/* The cabin is part of what the providers are asked: the group's own until the
              operator chooses another (which re-asks). */}
          <div className="grid grid-cols-2 gap-3">
            <label className="space-y-1">
              <span className="text-xs uppercase text-muted-foreground">
                {t('dialogs.override.cabin')}
              </span>
              <select
                className="w-full rounded-md border border-input bg-background px-2 py-1.5"
                value={shownCabin ?? ''}
                onChange={(e) => {
                  setCabin(asCabin(e.target.value));
                  setPicked(null);
                }}
                data-testid="drawer-override-cabin"
              >
                {shownCabin ? null : <option value="">{t('dialogs.override.cabinBooking')}</option>}
                {CABINS.map((c) => (
                  <option key={c} value={c}>
                    {t(`dialogs.override.cabinOption.${c}`)}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {manualMode && !shownCabin ? (
            <p className="text-xs text-muted-foreground" data-testid="drawer-override-cabin-required">
              {t('dialogs.override.cabinRequired')}
            </p>
          ) : null}

          {!manualMode ? (
            <FlightPicker
              groupId={group.subCaseUrn}
              caseUrn={group.caseUrn}
              cabin={cabin}
              seats={group.paxCount}
              defaultDate={day}
              defaultOrigin={pickerOrigin}
              defaultDestination={pickerDestination}
              selectedOfferRef={picked?.offerRef}
              onPick={(o) => {
                setPicked(o);
                setOfferError(null);
              }}
              onSearched={onSearched}
            />
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <label className="space-y-1">
                <span className="text-xs uppercase text-muted-foreground">
                  {t('dialogs.override.flightNumber')}
                </span>
                <input
                  className="w-full rounded-md border border-input bg-background px-2 py-1.5"
                  value={manualFlightNumber}
                  onChange={(e) => setManualFlightNumber(e.target.value)}
                  placeholder="LA123"
                />
              </label>
              <label className="space-y-1">
                <span className="text-xs uppercase text-muted-foreground">
                  {t('dialogs.override.departure')}
                </span>
                <input
                  type="datetime-local"
                  className="w-full rounded-md border border-input bg-background px-2 py-1.5"
                  value={manualDeparture}
                  onChange={(e) => setManualDeparture(e.target.value)}
                />
              </label>
            </div>
          )}

          <button
            type="button"
            className="text-xs text-primary underline-offset-2 hover:underline"
            onClick={() => {
              setManualMode((m) => !m);
              setPicked(null);
            }}
            data-testid="drawer-override-manual-toggle"
          >
            {manualMode
              ? t('dialogs.override.backToSearch')
              : t('dialogs.override.enterManually')}
          </button>

          {picked ? (
            <p className="rounded-md border border-primary/40 bg-primary/5 p-2 text-sm" data-testid="drawer-override-picked">
              {t('dialogs.override.picked')}{' '}
              <span className="font-mono font-semibold">{picked.segments.map(offerFlightCode).join(' · ')}</span>
              {carrierName(picked.segments[0]?.marketingAirline) ? (
                <span className="text-muted-foreground"> · {carrierName(picked.segments[0]?.marketingAirline)}</span>
              ) : null}{' '}
              — {[picked.segments[0]?.origin, ...picked.segments.map((seg) => seg.destination)].join(' → ')}
              {picked.provider ? (
                <span className="text-muted-foreground"> · {offerProviderName(tNetwork, picked.provider)}</span>
              ) : null}
            </p>
          ) : null}

          {offerError ? (
            <p
              className="rounded-md border border-amber-700/30 bg-amber-50 p-2 text-sm text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
              role="alert"
              data-testid="drawer-override-offer-error"
            >
              {offerError}
            </p>
          ) : null}
          <label className="block space-y-1">
            <span className="text-xs uppercase text-muted-foreground">
              {t('dialogs.override.reason')}
            </span>
            <textarea
              className="w-full rounded-md border border-input bg-background px-2 py-1.5"
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              placeholder={t('dialogs.override.reasonPlaceholder')}
            />
          </label>

          {/* POL-14: privileged policy-rule waiver. Default override is a compliant
              manual pick; checking this declares a knowing rule waiver, which the BFF
              permission-gates to Ops Supervisor. */}
          <div className="space-y-1.5 rounded-md border border-amber-300/50 bg-amber-50/50 p-2 dark:bg-amber-950/20">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={waiveRule}
                onChange={(e) => {
                  setWaiveRule(e.target.checked);
                  setPermError(null);
                }}
                data-testid="drawer-override-waive-rule"
              />
              <span>
                {t('dialogs.override.waive.pre')}{' '}
                <strong>{t('dialogs.override.waive.rule')}</strong>{' '}
                {t('dialogs.override.waive.post')}
              </span>
            </label>
            {waiveRule ? (
              <input
                className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm"
                value={waivedRule}
                onChange={(e) => setWaivedRule(e.target.value)}
                placeholder={t('dialogs.override.waivedRulePlaceholder')}
                aria-label={t('dialogs.override.waivedRuleLabel')}
                data-testid="drawer-override-waived-rule"
              />
            ) : null}
          </div>

          {permError ? (
            <p
              className="rounded-md border border-destructive/30 bg-destructive/5 p-2 text-sm text-destructive"
              role="alert"
              data-testid="drawer-override-perm-error"
            >
              {permError}
            </p>
          ) : null}

          {vendorError ? (
            <div
              className="space-y-1 rounded-md border border-amber-700/30 bg-amber-50 p-2 text-sm dark:bg-amber-900/30"
              role="alert"
              data-testid="drawer-override-vendor-error"
            >
              <p className="font-medium text-amber-800 dark:text-amber-300">
                {vendorError.message}
              </p>
              <p className="text-xs text-amber-800/80 dark:text-amber-300/80">
                {t(vendorNextStepKey)}
              </p>
              {vendorError.providerBacked ? (
                <p className="text-xs text-amber-800/80 dark:text-amber-300/80">
                  {t('dialogs.override.vendorErrorProviderOnly')}
                </p>
              ) : (
                <p className="text-xs text-amber-800/80 dark:text-amber-300/80">
                  {isSupervisor
                    ? t('dialogs.override.vendorErrorWaiverSelf')
                    : t('dialogs.override.vendorErrorWaiverAsk')}{' '}
                  <span className="font-mono">VENDOR_VALIDATION</span>{' '}
                  {t('dialogs.override.vendorErrorWaiverSuffix')}
                </p>
              )}
            </div>
          ) : null}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common:common.cancel')}
            </Button>
            <Button
              type="button"
              onClick={() => void submit()}
              disabled={!valid}
              data-testid="drawer-override-submit"
            >
              {override.isPending ? t('dialogs.override.saving') : t('dialogs.override.apply')}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ExcludeDialog({
  scope,
  group,
  open,
  onClose,
  onDone,
}: {
  scope: PnrGroupScope;
  group: DisruptionPnrGroupRow;
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation('roster');
  const exclude = useExcludePnr(scope);
  // Exclude routes through the PNR's parent disruption (disruption-scoped endpoint).
  const routingUrn = routingDisruptionUrn(scope, group);
  const [error, setError] = useState<string | null>(null);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="z-[60]">
        <DialogHeader>
          <DialogTitle>{t('dialogs.exclude.title', { pnr: group.pnrLabel })}</DialogTitle>
          <DialogDescription>
            {t('dialogs.exclude.description', {
              pax: formatPaxSummary(group.paxCount, group.paxBreakdown),
            })}
          </DialogDescription>
        </DialogHeader>
        {error ? (
          <p className="text-sm text-destructive" role="alert" data-testid="drawer-exclude-error">
            {error}
          </p>
        ) : null}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('dialogs.exclude.keep')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={exclude.isPending || !routingUrn}
            data-testid="drawer-exclude-confirm"
            onClick={async () => {
              if (!routingUrn) return;
              setError(null);
              try {
                await exclude.mutateAsync({
                  subCaseUrn: group.subCaseUrn,
                  caseUrn: group.caseUrn,
                  disruptionUrn: routingUrn,
                });
                onClose();
                onDone();
              } catch {
                setError(t('dialogs.exclude.error'));
              }
            }}
          >
            {exclude.isPending ? t('dialogs.exclude.pending') : t('dialogs.exclude.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Change-selection (cancel) confirm — undoes a SELECTED option (SELECTED→OVERRIDDEN,
 *  §10.4) so the operator can re-decide before ticketing. Reason is mandatory (audited). */
export function CancelSelectionDialog({
  scope,
  group,
  open,
  onClose,
  onLockConflict,
}: {
  scope: PnrGroupScope;
  group: DisruptionPnrGroupRow;
  open: boolean;
  onClose: () => void;
  onLockConflict: (holderName: string) => void;
}) {
  const { t } = useTranslation('roster');
  const cancel = useCancelGroupSelection(scope);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!open) {
      setReason('');
      setError(null);
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="z-[60]">
        <DialogHeader>
          <DialogTitle>{t('dialogs.cancelSelection.title', { pnr: group.pnrLabel })}</DialogTitle>
          <DialogDescription>{t('dialogs.cancelSelection.description')}</DialogDescription>
        </DialogHeader>
        <label className="block space-y-1 text-sm">
          <span className="text-xs uppercase text-muted-foreground">
            {t('dialogs.cancelSelection.reason')}
          </span>
          <textarea
            className="w-full rounded-md border border-input bg-background px-2 py-1.5"
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
            placeholder={t('dialogs.cancelSelection.reasonPlaceholder')}
          />
        </label>
        {error ? (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : null}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('dialogs.cancelSelection.keep')}
          </Button>
          <Button
            type="button"
            disabled={cancel.isPending || !reason.trim()}
            data-testid="drawer-cancel-confirm"
            onClick={async () => {
              setError(null);
              try {
                await cancel.mutateAsync({
                  subCaseUrn: group.subCaseUrn,
                  caseUrn: group.caseUrn,
                  reason: reason.trim(),
                });
                onClose();
              } catch (e) {
                if (isLockConflictError(e)) {
                  onLockConflict(e.holderName);
                  onClose();
                } else {
                  setError(t('dialogs.cancelSelection.error'));
                }
              }
            }}
          >
            {cancel.isPending
              ? t('dialogs.cancelSelection.pending')
              : t('dialogs.cancelSelection.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Reject-all confirm — marks the group's options unacceptable (OPTIONS_READY→
 *  NO_OPTIONS) to escalate / regenerate. The domain rejects the whole set; the BFF
 *  still requires a representative `optionUrn` on the body. */
export function RejectOptionsDialog({
  scope,
  group,
  optionUrn,
  open,
  onClose,
  onLockConflict,
}: {
  scope: PnrGroupScope;
  group: DisruptionPnrGroupRow;
  optionUrn?: string | undefined;
  open: boolean;
  onClose: () => void;
  onLockConflict: (holderName: string) => void;
}) {
  const { t } = useTranslation('roster');
  const reject = useRejectGroupOptions(scope);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!open) {
      setReason('');
      setError(null);
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="z-[60]">
        <DialogHeader>
          <DialogTitle>{t('dialogs.reject.title', { pnr: group.pnrLabel })}</DialogTitle>
          <DialogDescription>{t('dialogs.reject.description')}</DialogDescription>
        </DialogHeader>
        <label className="block space-y-1 text-sm">
          <span className="text-xs uppercase text-muted-foreground">
            {t('dialogs.reject.reason')}
          </span>
          <textarea
            className="w-full rounded-md border border-input bg-background px-2 py-1.5"
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('dialogs.reject.reasonPlaceholder')}
          />
        </label>
        {error ? (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : null}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('dialogs.reject.keep')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={reject.isPending || !optionUrn}
            data-testid="drawer-reject-confirm"
            onClick={async () => {
              if (!optionUrn) return;
              setError(null);
              try {
                await reject.mutateAsync({
                  subCaseUrn: group.subCaseUrn,
                  caseUrn: group.caseUrn,
                  optionUrn,
                  ...(reason.trim() ? { reason: reason.trim() } : {}),
                });
                onClose();
              } catch (e) {
                if (isLockConflictError(e)) {
                  onLockConflict(e.holderName);
                  onClose();
                } else {
                  setError(t('dialogs.reject.error'));
                }
              }
            }}
          >
            {reject.isPending ? t('dialogs.reject.pending') : t('dialogs.reject.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
